import React from "react";
import { Clock, Check } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetAttendanceByDateQuery } from "../../slices/attendanceApiSlice";
import { reverseGeocode } from "../../utils/geocode";
import { MapPin, Loader2 } from "lucide-react";

export default function TodayAttendanceStatus() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get role-based path prefix from current location
  const getRolePath = () => {
    const path = location.pathname;
    if (path.startsWith("/admin")) return "/admin";
    if (path.startsWith("/superadmin")) return "/superadmin";
    if (path.startsWith("/cfm")) return "/cfm";
    if (path.startsWith("/manager")) return "/manager";
    if (path.startsWith("/supervisor")) return "/supervisor";
    if (path.startsWith("/technician")) return "/technician";
    return "/technician"; // default fallback
  };

  const rolePath = getRolePath();

  // Skip this component for CFM and ADMIN users - they don't have personal attendance
  const isCfmOrAdmin = rolePath === "/admin" || rolePath === "/superadmin";

  // Get today's date in YYYY-MM-DD format (Asia/Thimphu timezone)
  const getTodayDateString = () => {
    const now = new Date();
    // Convert to Asia/Thimphu timezone
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
    );
    const year = thimphuDate.getFullYear();
    const month = String(thimphuDate.getMonth() + 1).padStart(2, "0");
    const day = String(thimphuDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = getTodayDateString();
  const { data: todayAttendanceData, isLoading } = useGetAttendanceByDateQuery(
    today,
    { skip: isCfmOrAdmin },
  );

  const [addresses, setAddresses] = React.useState({ in: null, out: null });
  const [loadingAddrs, setLoadingAddrs] = React.useState({ in: false, out: false });

  // Get entries from attendance data
  const entries = todayAttendanceData?.entries || [];
  const clockInEntry = entries.find((e) => e.entry_type === "CLOCK_IN");
  const clockOutEntry = entries.find((e) => e.entry_type === "CLOCK_OUT");

  React.useEffect(() => {
    const fetchAddrs = async () => {
      if (clockInEntry?.location?.latitude && clockInEntry?.location?.longitude) {
        setLoadingAddrs(prev => ({ ...prev, in: true }));
        try {
          const addr = await reverseGeocode(clockInEntry.location.latitude, clockInEntry.location.longitude);
          setAddresses(prev => ({ ...prev, in: addr }));
        } catch (e) {
          console.error(e);
        } finally {
          setLoadingAddrs(prev => ({ ...prev, in: false }));
        }
      }
      if (clockOutEntry?.location?.latitude && clockOutEntry?.location?.longitude) {
        setLoadingAddrs(prev => ({ ...prev, out: true }));
        try {
          const addr = await reverseGeocode(clockOutEntry.location.latitude, clockOutEntry.location.longitude);
          setAddresses(prev => ({ ...prev, out: addr }));
        } catch (e) {
          console.error(e);
        } finally {
          setLoadingAddrs(prev => ({ ...prev, out: false }));
        }
      }
    };
    if (entries.length > 0) fetchAddrs();
  }, [entries.length]);

  // Get attendance ID from the data
  const attendanceId =
    todayAttendanceData?.attendance?._id || todayAttendanceData?.attendance?.id;

  // Format time - backend stores Thimphu time as UTC, so we display it directly
  const formatTime = (timeString) => {
    if (!timeString) return null;
    try {
      return new Date(timeString).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Thimphu",
      });
    } catch {
      return null;
    }
  };

  // Format location - return full location string
  const formatLocation = (location) => {
    if (!location) return "N/A";
    // Try to build a full location string
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.state || location.region)
      parts.push(location.state || location.region);
    if (location.country) parts.push(location.country);
    if (parts.length > 0) return parts.join(", ");
    if (location.latitude && location.longitude) {
      return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
    }
    return "N/A";
  };

  // Don't show if no clock in/out entries
  if (isLoading || (!clockInEntry && !clockOutEntry)) {
    return null;
  }

  return (
    <div className="mt-6">
      <h2 className="text-2xl font-bold text-zinc-800 mb-0">Today</h2>
      <p className="text-zinc-600 mb-4 text-sm">Activities for today</p>

      <div className="space-y-0 border-t border-zinc-200 pt-3">
        {/* Clock Out Entry */}
        {clockOutEntry && (
          <div
            className="flex items-center gap-4 py-3 border-b border-zinc-200 cursor-pointer hover:bg-zinc-50 -mx-4 px-4 rounded transition-colors"
            onClick={() => {
              if (attendanceId) {
                navigate(`${rolePath}/attendance/record/${attendanceId}`);
              }
            }}
          >
            <div className="w-10 h-10 rounded-full border-2 border-orange-300 flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-zinc-800 mb-0.5">
                Clocked out
              </div>
              <div className="text-sm text-zinc-600 flex items-center gap-1.5 truncate">
                <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                {loadingAddrs.out ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span className="text-xs italic">Locating...</span>
                  </span>
                ) : (
                  addresses.out || formatLocation(clockOutEntry.location)
                )}
              </div>
            </div>
            <div className="text-sm font-medium text-zinc-800 flex-shrink-0">
              {formatTime(clockOutEntry.time) || "N/A"}
            </div>
          </div>
        )}

        {/* Clock In Entry */}
        {clockInEntry && (
          <div
            className="flex items-center gap-4 py-3 border-b border-zinc-200 cursor-pointer hover:bg-zinc-50 -mx-4 px-4 rounded transition-colors"
            onClick={() => {
              if (attendanceId) {
                navigate(`${rolePath}/attendance/record/${attendanceId}`);
              }
            }}
          >
            <div className="w-10 h-10 rounded-full border-2 border-blue-300 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-zinc-800 mb-0.5">
                Clocked in
              </div>
              <div className="text-sm text-zinc-600 flex items-center gap-1.5 truncate">
                <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                {loadingAddrs.in ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span className="text-xs italic">Locating...</span>
                  </span>
                ) : (
                  addresses.in || formatLocation(clockInEntry.location)
                )}
              </div>
            </div>
            <div className="text-sm font-medium text-zinc-800 flex-shrink-0">
              {formatTime(clockInEntry.time) || "N/A"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
